import {makeAutoObservable, reaction, runInAction} from 'mobx';
import {settingsStore} from '@/shared/stores/settingsStore';
import {userStore} from '@/shared/stores/userStore';

interface TeacherStatusCache {
    isTeacher: boolean;
    lastCheck: number;
}

class IsTeacherStore {
    private _teacherCache: Map<string | null, TeacherStatusCache> = new Map();
    private _loading: Map<string | null, boolean> = new Map();
    private _error: string | null = null;
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    private _disposer: () => void;

    constructor() {
        makeAutoObservable(this);

        // Set up reaction to observe classroomApiUrl and idString changes
        this._disposer = reaction(
            () => ({
                classroomApiUrl: settingsStore.classroomApiUrl,
                idString: userStore.idString,
            }),
            () => {
                // Clear cache when dependencies change, will trigger re-fetch on next access
                this._teacherCache.clear();
            },
        );
    }

    /**
     * Initialize the store by checking teacher status
     * Call this after dependencies (settingsStore, userStore) are ready
     */
    initialize(): void {
        if (!this._teacherCache.has(null)) {
            this.checkIsTeacher();
        }
    }

    get isTeacher(): boolean | null {
        const cached = this._teacherCache.get(null);

        if (!cached || Date.now() - cached.lastCheck >= this.CACHE_DURATION) {
            this.checkIsTeacher();
        }

        return cached?.isTeacher ?? null;
    }

    isTeacherForCourse(courseId: string): boolean | null {
        const cached = this._teacherCache.get(courseId);

        if (!cached || Date.now() - cached.lastCheck >= this.CACHE_DURATION) {
            this.checkIsTeacher(courseId);
        }

        return cached?.isTeacher ?? null;
    }

    get loading(): boolean {
        return this._loading.get(null) ?? false;
    }

    isLoadingForCourse(courseId: string): boolean {
        return this._loading.get(courseId) ?? false;
    }

    get error(): string | null {
        return this._error;
    }

    private setError(error: string | null): void {
        this._error = error;
    }

    private setLoading(key: string | null, loading: boolean): void {
        this._loading.set(key, loading);
    }

    private setTeacherStatus(key: string | null, isTeacher: boolean): void {
        this._teacherCache.set(key, {
            isTeacher,
            lastCheck: Date.now(),
        });
    }

    private async checkIsTeacher(courseId?: string): Promise<void> {
        const cacheKey = courseId ?? null;
        const idString = userStore.idString;

        if (!idString) {
            return;
        }

        // Check cache
        const cached = this._teacherCache.get(cacheKey);
        if (cached && Date.now() - cached.lastCheck < this.CACHE_DURATION) {
            return;
        }

        const classroomApiUrl = settingsStore.classroomApiUrl;
        if (!classroomApiUrl) {
            return;
        }

        const accessToken = userStore.accessToken;
        if (!accessToken) {
            this.setError('No access token available');
            return;
        }

        this.setLoading(cacheKey, true);
        this.setError(null);

        try {
            const url = courseId
                ? `${classroomApiUrl}/api/1.0/isTeacher?idString=${idString}&courseId=${courseId}`
                : `${classroomApiUrl}/api/1.0/isTeacher?idString=${idString}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            runInAction(() => {
                this.setTeacherStatus(cacheKey, data);
            });
        } catch (error) {
            runInAction(() => {
                this.setError(error instanceof Error ? error.message : 'An unknown error occurred');
            });
        } finally {
            runInAction(() => {
                this.setLoading(cacheKey, false);
            });
        }
    }

    reset(): void {
        this._teacherCache.clear();
        this._loading.clear();
        this._error = null;
    }

    dispose(): void {
        if (this._disposer) {
            this._disposer();
        }
    }
}

export const isTeacherStore = new IsTeacherStore();
